import {
  DEFAULT_EXCHANGE_AGENT_USAGE_FEE,
  DEFAULT_EXCHANGE_AGENT_USAGE_FEE_PERCENT,
  ExchangeAgentBillingFeeType,
  ExchangeAgentBillingSettings,
  ExchangeAgentBillingType,
} from "@redotech/redo-billing-common/team-billing";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { numberValidator } from "@redotech/ui/validators";
import { assertNever } from "@redotech/util/type";
import { memo } from "react";

export enum ExchangeAgentBillingTier {
  FREE = "FREE",
  BILLED = "PAID",
  DISABLED = "DISABLED",
}
const exchangeAgentBillingTierOptions = {
  [ExchangeAgentBillingTier.FREE]: {
    type: "text" as const,
    id: ExchangeAgentBillingTier.FREE,
    text: "Free",
    value: ExchangeAgentBillingTier.FREE,
    supportingText: "AI Exchange Agent is available for free.",
  },
  [ExchangeAgentBillingTier.BILLED]: {
    type: "text" as const,
    id: ExchangeAgentBillingTier.BILLED,
    text: "Paid",
    value: ExchangeAgentBillingTier.BILLED,
    supportingText: "AI Exchange Agent is available and billed per exchange.",
  },
  [ExchangeAgentBillingTier.DISABLED]: {
    type: "text" as const,
    id: ExchangeAgentBillingTier.DISABLED,
    text: "Disabled",
    value: ExchangeAgentBillingTier.DISABLED,
    supportingText:
      "AI Exchange Agent is disabled (only use if churned off of paid tier)",
  },
};

export const exchangeAgentBillingSettingsForm = groupInput({
  billingTier: input<ExchangeAgentBillingTier>(),
  enabled: input<boolean>(),
  enabledBilling: input<boolean>(),
  renewOnPeriodEnd: input<boolean>(),
  startDate: input<Date>(),
  usageFee: input<string>({ validator: numberValidator({ min: 0 }) }),
  usageFeePercent: input<string>({
    validator: numberValidator({ min: 0, max: 100 }),
  }),
  billingFeeType: input<ExchangeAgentBillingFeeType>(),
  billingType: input<ExchangeAgentBillingType>(),
});

export const exchangeAgentBillingConfigSettingsDefault = {
  billingTier: ExchangeAgentBillingTier.FREE,
  enabled: true,
  enabledBilling: false,
  renewOnPeriodEnd: false,
  startDate: new Date(),
  usageFee: `${DEFAULT_EXCHANGE_AGENT_USAGE_FEE}`,
  usageFeePercent: `${DEFAULT_EXCHANGE_AGENT_USAGE_FEE_PERCENT}`,
  billingFeeType: "flat" as ExchangeAgentBillingFeeType,
  billingType: "shopify" as ExchangeAgentBillingType,
};

export type ExchangeAgentBillingSettingsForm = InputSchema.Input<
  typeof exchangeAgentBillingSettingsForm
>;
export type ExchangeAgentBillingSettingsValue = InputSchema.Value<
  typeof exchangeAgentBillingSettingsForm
>;

const billingFeeTypeOptions = {
  flat: {
    type: "text" as const,
    id: "flat",
    text: "Flat Fee",
    value: "flat" as ExchangeAgentBillingFeeType,
    supportingText: "Charge a fixed amount per exchange.",
  },
  percentage: {
    type: "text" as const,
    id: "percentage",
    text: "Percentage",
    value: "percentage" as ExchangeAgentBillingFeeType,
    supportingText: "Charge a percentage of the exchange value.",
  },
};

const billingMethodOptions = {
  shopify: {
    type: "text" as const,
    id: "shopify",
    text: "Shopify App Usage",
    value: "shopify" as ExchangeAgentBillingType,
    supportingText: "Bill via Shopify App Usage (aggregated billing).",
  },
  payout_order: {
    type: "text" as const,
    id: "payout_order",
    text: "Payout Order",
    value: "payout_order" as ExchangeAgentBillingType,
    supportingText:
      "Bill via payout orders per exchange (charges immediately).",
  },
};

export const ExchangeAgentBillingSettingsCard = memo(
  function ExchangeAgentBillingSettingsCard({
    input,
  }: {
    input: ExchangeAgentBillingSettingsForm;
  }) {
    const {
      billingTier,
      enabled,
      enabledBilling,
      renewOnPeriodEnd,
      startDate,
      usageFee,
      usageFeePercent,
      billingFeeType,
      billingType,
    } = input.inputs;

    const currentlyBilling = enabledBilling.value && renewOnPeriodEnd.value;

    return (
      <>
        <Flex dir="column">
          <Text>Billing Tier</Text>
          <RedoSingleSelectDropdownInput
            options={Object.values(exchangeAgentBillingTierOptions)}
            optionSelected={({ item }) =>
              changeExchangeAgentBillingTier(input, item.value)
            }
            selectedItem={exchangeAgentBillingTierOptions[billingTier.value]}
            size="sm"
          />
        </Flex>
        <Flex justify="space-between" overflow="auto">
          <Text fontWeight="medium">{`Exchange Agent: ${enabled.value ? "Enabled" : "Disabled"}`}</Text>
          <Text fontWeight="medium">{`Billing: ${currentlyBilling ? "Enabled" : "Disabled"}`}</Text>
        </Flex>
        {currentlyBilling && (
          <>
            <Flex dir="column">
              <Text>Billing Method</Text>
              <RedoSingleSelectDropdownInput
                options={Object.values(billingMethodOptions)}
                optionSelected={({ item }) => billingType.setValue(item.value)}
                selectedItem={billingMethodOptions[billingType.value]}
                size="sm"
              />
            </Flex>
            <Flex dir="column">
              <Text>Billing Fee Type</Text>
              <RedoSingleSelectDropdownInput
                options={Object.values(billingFeeTypeOptions)}
                optionSelected={({ item }) =>
                  billingFeeType.setValue(item.value)
                }
                selectedItem={billingFeeTypeOptions[billingFeeType.value]}
                size="sm"
              />
            </Flex>
            {billingFeeType.value === "flat" ? (
              <>
                <Flex>
                  <RedoTextInput
                    IconTrailing={() => <Text as="span">USD</Text>}
                    label="Usage Fee (per exchange)"
                    min={0}
                    placeholder="0.00"
                    setValue={usageFee.setValue}
                    size="md"
                    step={0.01}
                    value={usageFee.value}
                  />
                </Flex>
                {usageFee.errors.length > 0 && (
                  <Text fontSize="xs" textColor="error">
                    {usageFee.errors[0]}
                  </Text>
                )}
              </>
            ) : (
              <>
                <Flex>
                  <RedoTextInput
                    IconTrailing={() => <Text as="span">%</Text>}
                    label="Usage Fee Percentage (of exchange value)"
                    max={100}
                    min={0}
                    placeholder="0"
                    setValue={usageFeePercent.setValue}
                    size="md"
                    step={0.1}
                    value={usageFeePercent.value}
                  />
                </Flex>
                {usageFeePercent.errors.length > 0 && (
                  <Text fontSize="xs" textColor="error">
                    {usageFeePercent.errors[0]}
                  </Text>
                )}
              </>
            )}
            <Flex dir="column">
              <Text>Billing Start Date</Text>
              <Text fontSize="xs" textColor="secondary">
                Every exchange since this date will be billed. You should only
                change this if you are enabling billing for the first time or
                re-enabling billing after a period of time of not billing.
              </Text>
              <Flex>
                <RedoSingleDatePicker
                  buttonSize="md"
                  date={Temporal.PlainDate.from(
                    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                    startDate.value.toISOString().split("T")[0],
                  )}
                  setDate={(date) =>
                    startDate.setValue(new Date(date.toString()))
                  }
                />
              </Flex>
            </Flex>
          </>
        )}
      </>
    );
  },
);

function changeExchangeAgentBillingTier(
  input: ExchangeAgentBillingSettingsForm,
  billingTier: ExchangeAgentBillingTier,
) {
  if (input.inputs.billingTier.value === billingTier) {
    return;
  }

  input.inputs.billingTier.setValue(billingTier);
  switch (billingTier) {
    case ExchangeAgentBillingTier.FREE: {
      input.inputs.enabled.setValue(true);
      input.inputs.enabledBilling.setValue(false);
      input.inputs.renewOnPeriodEnd.setValue(false);
      break;
    }
    case ExchangeAgentBillingTier.BILLED: {
      input.inputs.enabled.setValue(true);
      input.inputs.enabledBilling.setValue(true);
      input.inputs.renewOnPeriodEnd.setValue(true);
      break;
    }
    case ExchangeAgentBillingTier.DISABLED: {
      input.inputs.enabled.setValue(false);
      // NOTE: We do not want to set enabledBilling to false here
      // because we still want to bill for the existing billing window.
      input.inputs.renewOnPeriodEnd.setValue(false);
      break;
    }
    default:
      assertNever(billingTier);
  }
}

export function createExchangeAgentBillingSettingsFormValue(
  loadedSettings: ExchangeAgentBillingSettings,
): ExchangeAgentBillingSettingsValue {
  let billingTier;
  if (!loadedSettings.enabled) {
    billingTier = ExchangeAgentBillingTier.DISABLED;
  } else if (loadedSettings.enableBilling && loadedSettings.renewOnPeriodEnd) {
    billingTier = ExchangeAgentBillingTier.BILLED;
  } else {
    billingTier = ExchangeAgentBillingTier.FREE;
  }

  return {
    billingTier,
    enabled: loadedSettings.enabled,
    enabledBilling: loadedSettings.enableBilling,
    renewOnPeriodEnd: loadedSettings.renewOnPeriodEnd,
    startDate: loadedSettings.startDate
      ? new Date(loadedSettings.startDate)
      : new Date(),
    usageFee: loadedSettings.usageFee.toString(),
    usageFeePercent: (loadedSettings.usageFeePercent ?? 0).toString(),
    billingFeeType: loadedSettings.billingFeeType ?? "flat",
    billingType: loadedSettings.billingType ?? "shopify",
  };
}

export function getUpdatedExchangeAgentBillingSettings(
  existing: ExchangeAgentBillingSettings,
  newSettings: ExchangeAgentBillingSettingsValue,
): ExchangeAgentBillingSettings {
  let enabled = existing.enabled;
  let enableBilling = existing.enableBilling;
  let renewOnPeriodEnd = existing.renewOnPeriodEnd;

  switch (newSettings.billingTier) {
    case ExchangeAgentBillingTier.FREE: {
      enabled = true;
      enableBilling = false;
      renewOnPeriodEnd = false;
      break;
    }
    case ExchangeAgentBillingTier.BILLED: {
      enabled = true;
      enableBilling = true;
      renewOnPeriodEnd = true;
      break;
    }
    case ExchangeAgentBillingTier.DISABLED: {
      enabled = false;

      // Billing was on and being turned off.
      if (existing.enableBilling && existing.renewOnPeriodEnd) {
        // We don't turn off enableBilling here because we still want to bill for the existing billing window.
        renewOnPeriodEnd = false;
      }
      break;
    }
    default:
      assertNever(newSettings.billingTier);
  }

  return {
    enabled,
    enableBilling,
    renewOnPeriodEnd,
    startDate: newSettings.startDate,
    usageFee: isNaN(Number(newSettings.usageFee))
      ? DEFAULT_EXCHANGE_AGENT_USAGE_FEE
      : Number(newSettings.usageFee),
    usageFeePercent: isNaN(Number(newSettings.usageFeePercent))
      ? DEFAULT_EXCHANGE_AGENT_USAGE_FEE_PERCENT
      : Number(newSettings.usageFeePercent),
    billingFeeType: newSettings.billingFeeType,
    billingType: newSettings.billingType,
    period: existing.period,
    periodFeeInCents: existing.periodFeeInCents,
  };
}
